// src/hooks/drawing/useRedraw.js
import useStore from '../../store';
import chroma from 'chroma-js';

import { freeMemory } from './imageCached';

import { createFill } from './brushes/filler';
import { drawPlainMarkerStroke } from './brushes/marker';

import { drawDashedStroke } from './brushes/dashed';
import { drawOutlinedStroke } from './brushes/outlined';
import { drawSprayStroke } from './brushes/spray';
import { drawSprayNoBlurStroke } from './brushes/sprayNoBlur';
import { drawFeatherStroke } from './brushes/feather';
import { drawPencilStroke } from './brushes/pencil';
import { drawOilStroke } from './brushes/oil';
import { drawWatercolorStroke } from './brushes/watercolor';
import { drawBristleStroke } from './brushes/bristle';
import { drawRembrandtStroke } from './brushes/rembrandt';
import { drawBlurStroke } from './brushes/blur';
import { drawEffectStroke } from './brushes/effect';
import { drawSparkleStroke } from './brushes/sparkle';
import { drawNeonStroke } from './brushes/neon';
import { drawTextureStroke } from './brushes/texture';
import { drawGlyphStroke } from './brushes/glyph';
import { drawPixelBrushStroke } from './brushes/pixels';
import { drawWetStroke } from './brushes/wet';
import { drawVelvetPastelStroke } from './brushes/velvetPastel';
import { drawOilFlatStroke } from './brushes/flat';
import { drawBlenderStroke } from './brushes/blender';
import { drawTestStroke } from './brushes/test';
import { drawWaterSpreadStroke } from './brushes/waterSpread';
import { drawWaterSoftStroke } from './brushes/waterSoft';


export const useRedraw = (ref) => {
  const { menu, debug, mount, telegram, info, convy, drawing, brush, history } = ref;
  const { game } = info;
  const { showTemporaryHint, forceRender } = menu.methods;
  const { isAppleDevice, serverTimeFun } = info.methods;
  const { wipeTempCanvas } = drawing.methods;
  const { userStrokes } = history;

  // Для кеширования теперь используем отдельное хранилище для каждого слоя:
  if (!history.layerImageCache) {
    history.layerImageCache = {}; // { [layerId]: Map() }
  }

  // Функция отрисовки фона
  function drawBackground({ context, color }) {
    context.save();
    context.fillStyle = color;
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    context.restore();
  }

  // Универсальная функция отрисовки штриха (с нормализацией точек)
  async function drawStroke(stroke, context) {

    stroke = { ...stroke };
    if (stroke.points) {
      if (!Array.isArray(stroke.points)) return;
      stroke.points = stroke.points.map(point =>
        Array.isArray(point) ? { x: point[0], y: point[1] } : point
      );
    }
    const { brush, effect, sets = {} } = stroke;
    const softness = sets?.softness || stroke.softness || 0;
    const params = { isApple: isAppleDevice() };

    context.save();
    context.globalCompositeOperation = 'source-over';

    if (stroke.tool === 'eraser' && !stroke.oldCanvas) {
      context.globalCompositeOperation = 'destination-out';
    }

    if (['feather', 'ink'].includes(brush)) {
      await drawFeatherStroke(stroke, context, params);
    } else if (brush === 'pencil') {
      await drawPencilStroke(stroke, context, params);
    } else if (brush === 'oil') {
      await drawOilStroke(stroke, context, params);
    } else if (brush === 'watercolor') {
      await drawWatercolorStroke(stroke, context, params);
    } else if (brush === 'bristle') {
      await drawBristleStroke(stroke, context, params);
    } else if (brush === 'rembrandt') {
      await drawRembrandtStroke(stroke, context, params);
    } else if (effect === 'blur' || brush === 'blur') {
      await drawBlurStroke(stroke, context, params);
    } else if (effect === 'texture') {
      await drawTextureStroke(stroke, context, params);
    } else if (effect === 'smudge') {
      await drawBlenderStroke(stroke, context, { ...params, sourceCtx: context });
    } else if (effect === 'noise' || brush === 'noise') {
      await drawEffectStroke({ ...stroke, effect: 'noise' }, context, params);
    } else if (effect) {
      await drawEffectStroke(stroke, context, params);
    } else if (brush === 'dashed') {
      await drawDashedStroke(stroke, context, params);
    } else if (brush === 'outlined') {
      await drawOutlinedStroke(stroke, context, params);
    } else if (brush === 'neon') {
      await drawNeonStroke(stroke, context, params);
    } else if (brush === 'sparkle') {
      await drawSparkleStroke(stroke, context, params);
    } else if (brush === 'glyph') {
      await drawGlyphStroke(stroke, context, params);
    } else if (brush === 'pixels') {
      await drawPixelBrushStroke(stroke, context, params);
    } else if (brush === 'wet') {
      await drawWetStroke(stroke, context, params);
    } else if (brush === 'flat') {
      await drawOilFlatStroke(stroke, context, params);
    } else if (brush === 'velvetPastel') {
      await drawVelvetPastelStroke(stroke, context, params);
    } else if (brush === 'blender') {
      await drawBlenderStroke(stroke, context, { ...params, sourceCtx: context });
    } else if (brush === 'waterSpread') {
      await drawWaterSpreadStroke(stroke, context, params);
    } else if (brush === 'waterSoft') {
      await drawWaterSoftStroke(stroke, context, params);
    } else if (brush === 'test') {
      await drawTestStroke(stroke, context, params);
      // Можно добавить свою логику для тестовой кисти
    } else if (brush === 'spray' || softness > 0) {
      // if (isAppleDevice() && sets.appleSprayFixFinal) {
      if (isAppleDevice()) {
        await drawSprayStroke(stroke, context, convy.soft.ctx, params);
        // await drawSprayNoBlurStroke(stroke, context, convy.soft.ctx);
      } else {
        await drawSprayStroke(stroke, context, convy.soft.ctx, params);
      }
    } else {
      await drawPlainMarkerStroke(stroke, context, params);
    }
    context.restore();
  }

  // Функция выполнения заливки (fill) – теперь заливка происходит в том слое, который указан в stroke.layerId
  function makeFill({ context, stroke }) {
    try {
      createFill(
        stroke,
        context,
      );
      saveCache(context, stroke, stroke.layerId || 'base');
      stroke.rendered = true;
    } catch (e) {
      console.error(e);
    }
  }

  // Функция кеширования: сохраняет offscreen-канвас для последнего штриха в данном слое

  function saveCache({
    layerCtx, 
    lastStroke, 
    layerId, 
  }) {

    if (!lastStroke) return;
    const canvas = layerCtx.canvas;
    const layerCache = history.layerImageCache[layerId];
  
    // Создаём offscreen-канвас с такими же размерами, как исходный
    const cacheCanvas = document.createElement('canvas');
    cacheCanvas.width = canvas.width;
    cacheCanvas.height = canvas.height;
    const cacheCtx = cacheCanvas.getContext('2d');
  
    // Копируем текущее содержимое канваса
    cacheCtx.drawImage(canvas, 0, 0);
    // if (lastStroke.time !== history.lastRenderedStroke.time) { return; }

    layerCache.set(lastStroke.time, {
      time: Date.now(),
      canvas: cacheCanvas,
    });
    window.croco?.log(`🗂cached: ${lastStroke.brush}: ${lastStroke.time} | ${layerId}`);
  
    const activeLayer = convy.methods.getActiveLayer();

    freeMemory({ 
      imageCache: history.layerImageCache, 
      activeLayerId: activeLayer.id,
     });
  
    cacheCanvas.toBlob(async (blob) => {
      if (!blob) return;
      
      // Сохраняем Blob в IndexedDB
      // mount.methods.addLayerCache(telegram.gameId, layerId, lastStroke.time, blob)
      mount.methods.addLayerCache({
        gameId: telegram.gameId, 
        layerId, 
        cacheId: lastStroke.time, 
        blob, 
        activeLayerId: activeLayer.id,
      })
      .then((id) => {
        window.croco?.log(`🗄+IDB layer ${lastStroke.brush}: ${lastStroke.time}`);
      })
      .catch((err) => {
        window.croco?.log(`❌error IDB layer ${lastStroke.brush}: ${lastStroke.time}`);
      });
  
      // Отправляем бинарные данные по сокету (ключ изменён на imageWebp)
      mount.methods.saveLayerToServer({
        layerId,
        bufferWebp: blob,
        time: lastStroke.time,
      })
      .catch((err) => {
        console.error('❌🌐 Error saving layer cache to server', err);
      });

    }, 'image/png', 0.5);
  }


  // Основная функция перерисовки: теперь рендерим по каждому слою отдельно, а затем компонуем на основном канвасе
  const redraw = async(parm = {}) =>{

    history.playerCount = Object.keys(userStrokes || {}).length;
    if (!convy.main.ref || !convy.main.ctx) {
      return setTimeout(() => { redraw(); }, 1500);
    };
    if (drawing.isDrawing && !parm.preview) return;
    if (history.isRendering) return;

    history.isRendering = { started: Date.now(), };

    debug.metrics.redraw = {
      started: Date.now(),
      strokes: 0,
    };

    await redrawMultipleLayers(parm);
    
    debug.metrics.draw.push({
      ss: debug.metrics.redraw.strokes,
      dif: debug.metrics.redraw.difficulty,
      sdif: debug.metrics.redraw.saveDifficulty,
      tm: debug.metrics.redraw.mle,
      id: history.lastRenderedStroke?.time % 10000,
    });
    delete debug.metrics.redraw;
    
    menu.methods.render();

    history.isRendering = null;



  }


  const redrawMultipleLayers = async (parm = {}) => {
    const mainCanvas = convy.main.ref;
    const mainCtx = convy.main.ctx;

    const { activeTool } = useStore.getState();

    const bannedUsers = Object.entries(game.users)
    .filter(([userKey, user]) => user?.sets?.banStrokes)
    .map(([userKey])=>userKey);
  
    // Получаем настройки групп слоев
    const gameSettings = info.game.settings || {};
    const layerGroups = gameSettings.layerGroups || {};
    const layerOrder = gameSettings.layerOrder || [];
    
    // Функция совместимости - создает layerOrder если его нет
    const ensureLayerOrder = () => {
      if (layerOrder.length === 0) {
        const oldLayerOrder = Object.keys(convy.layerInfo)
        .sort((a, b) => convy.layerInfo[b].order - convy.layerInfo[a].order);
        return oldLayerOrder;
      }
      return layerOrder;
    };
    
    const actualLayerOrder = ensureLayerOrder();
    
    // Функция для проверки видимости слоя с учетом групп
    const isLayerVisibleWithGroups = (layerData) => {
      // Сначала проверяем собственную видимость слоя
      if (!layerData.visible || layerData.deleted) return false;
      
      // Если слой в группе, проверяем видимость группы
      if (layerData.groupId && layerGroups[layerData.groupId]) {
        return layerGroups[layerData.groupId].visible !== false;
      }
      
      return true;
    };
  
    // Получаем список слоёв из convy.layerInfo и фильтруем по видимости с учетом групп
    const layersObj = convy.layerInfo || {};
    const allLayersArray = Object.keys(layersObj)
      .map(id => ({ id, ...layersObj[id] }))
      .filter(isLayerVisibleWithGroups);
    
    // Сортируем слои согласно layerOrder (только те что есть в layerOrder)
    const layersArray = actualLayerOrder
      .map(id => allLayersArray.find(layer => layer.id === id))
      .filter(Boolean);

    // [FIX] Mafia Spy View: Добавляем виртуальные слои игроков, которых нет в layerOrder
    if (info.game?.mode === 'mafia') {
      const extraLayers = allLayersArray.filter(l => !actualLayerOrder.includes(l.id) && l.visible);
      layersArray.push(...extraLayers);
    }

    debug.metrics.redraw.dls = Date.now() - debug.metrics.redraw.started;
  
    // Получаем подготовленные штрихи для всех слоёв
    const strokesData = drawing.methods.prepareStrokesForAllLayers();
  
    // Для каждого слоя:
    for (const layerData of layersArray) {
      const layerId = layerData.id;

      const layer = ref.convy.methods.getLayer(layerId);
      const { canvas: layerCanvas, ctx: layerCtx, info: layerInfo } = layer;
  
      // Если слой выключен или уже готов – пропускаем его отрисовку
      if (!layerInfo.visible) continue;
      if (layer.ready) continue;
  
      // Очищаем канвас слоя
      layerCtx.save();
      layerCtx.setTransform(1, 0, 0, 1, 0, 0);
      layerCtx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);
      layerCtx.restore();

      const { strokes, difficulty, saveDifficulty } = strokesData[layerId];

      if (difficulty) {
        // Получаем для слоя подготовленные штрихи и сложность
        debug.metrics.redraw.difficulty = difficulty;
        debug.metrics.redraw.saveDifficulty = saveDifficulty;
      }

      await drawOnLayer({
        layerCtx, 
        layerData, 
        strokes, 
        difficulty, 
        bannedUsers,
        ...parm,
      });

    }
  
    debug.metrics.redraw.dle = Date.now() - debug.metrics.redraw.started;
  
    // Очищаем основной канвас
    mainCtx.clearRect(0, 0, mainCtx.canvas.width, mainCtx.canvas.height);
    mainCtx.fillStyle = info.game?.settings?.backgroundColor || '#FFFFFFFF';
    mainCtx.fillRect(0, 0, mainCanvas.width, mainCanvas.height);
  
    if (!parm.preview) { wipeTempCanvas(); }

    layersArray.forEach(layerData=>{
      const currentLayer = convy.methods.getLayer(layerData.id);
      delete currentLayer.maskTempCanvas;
      delete currentLayer.maskTempCtx;
      delete currentLayer.previewTempCanvas;
      delete currentLayer.previewTempCtx;
    })

    const activeLayer = convy.methods.getActiveLayer();
    if (parm.preview) {
      drawActivePreview({ activeLayer, activeTool });
    }

    // Функция для сортировки слоев с учетом групп - теперь полностью на layerOrder
    const sortLayersWithGroups = (layers) => {
      // Группируем слои по группам и вне групп
      const layersInGroups = {};
      const layersWithoutGroup = [];
      
      layers.forEach(layer => {
        if (layer.groupId && layerGroups[layer.groupId]) {
          layersInGroups[layer.groupId] ??= [];
          layersInGroups[layer.groupId].push(layer);
        } else {
          layersWithoutGroup.push(layer);
        }
      });
      
      // Сортируем слои внутри каждой группы согласно layerOrder
      Object.keys(layersInGroups).forEach(groupId => {
        layersInGroups[groupId].sort((a, b) => {
          const aIndex = actualLayerOrder.indexOf(a.id);
          const bIndex = actualLayerOrder.indexOf(b.id);
          return aIndex - bIndex;
        });
      });
      
      // Сортируем слои вне групп согласно layerOrder  
      layersWithoutGroup.sort((a, b) => {
        const aIndex = actualLayerOrder.indexOf(a.id);
        const bIndex = actualLayerOrder.indexOf(b.id);
        return aIndex - bIndex;
      });
      
      // Создаем финальный порядок согласно layerOrder
      const finalOrder = [];
      const processedItems = new Set();
      
      // Обрабатываем элементы согласно layerOrder
      actualLayerOrder.forEach(itemId => {
        if (layerGroups[itemId]) {
          // Это группа - добавляем все её слои
          if (layersInGroups[itemId]) {
            layersInGroups[itemId].forEach(layer => {
              finalOrder.unshift(layer);
              processedItems.add(layer.id);
            });
          }
          processedItems.add(itemId);
        } else {
          // Это отдельный слой
          const layer = layersWithoutGroup.find(l => l.id === itemId);
          if (layer) {
            finalOrder.unshift(layer);
            processedItems.add(layer.id);
          }
        }
      });
      
      
      return finalOrder;
    };

    // Сортируем слои с учетом групп и получаем только основные слои (не маски)
    const mainLayers = sortLayersWithGroups(layersArray.filter(layer => !layer.clippingFor));

    for (const layerData of mainLayers) {

      const layerId = layerData.id;
      const layer = convy.methods.getLayer(layerId);

      drawMasks(layer, layersArray);

      mainCtx.save();
      transformation(mainCtx, layerData);

      const layerCanvas = (layer.maskTempCanvas || layer.previewTempCanvas || layer.canvas);
      mainCtx.drawImage(layerCanvas, -layerCanvas.width / 2, -layerCanvas.height / 2);
      mainCtx.restore();

    }

    debug.metrics.redraw.mle = Date.now() - debug.metrics.redraw.started;

    updatePixelGrid();
  };



  const drawActivePreview = ({ activeLayer, activeTool }) => {
    const { effectType } = useStore.getState();

    if (activeTool === 'effect' && effectType === 'smudge' && convy.preview.containsBase) {
      activeLayer.previewTempCanvas = convy.preview.canvas;
      activeLayer.previewTempCtx = convy.preview.ctx;
      return;
    }
    
    convy.clone.canvas.width = activeLayer.canvas.width;
    convy.clone.canvas.height = activeLayer.canvas.height;

    convy.clone.ctx.globalCompositeOperation = 'source-over';
    convy.clone.ctx.clearRect(0, 0, convy.clone.canvas.width, convy.clone.canvas.height);

    convy.clone.ctx.drawImage(activeLayer.canvas, 0, 0);

    if (activeTool === 'eraser') {
      convy.clone.ctx.globalCompositeOperation = 'destination-out';
    }
    convy.clone.ctx.drawImage(convy.preview.canvas, 0, 0);

    activeLayer.previewTempCanvas = convy.clone.canvas;
    activeLayer.previewTempCtx = convy.clone.ctx;

  }

  const drawMasks = (mainLayer, layersArray) =>{

     // maskGroup
    const attachedMasks = layersArray
     .filter(layerData => layerData.clippingFor === mainLayer.id)
     .sort((a, b) => a.order - b.order)
     .map(layerData=>convy.methods.getLayer(layerData.id));

    if (!attachedMasks[0]) { return; }

    // Creating Mask Temp Canvas
    const maskTempCanvas = document.createElement('canvas');
    maskTempCanvas.width = mainLayer.canvas.width;
    maskTempCanvas.height = mainLayer.canvas.height;
    const maskTempCtx = maskTempCanvas.getContext('2d');

    // То что было
    maskTempCtx.drawImage(mainLayer.previewTempCanvas || mainLayer.canvas, 0, -0);

    // Накладываем все слои
    maskTempCtx.save();

    attachedMasks.forEach(maskLayer=>{
      
      addOpacityAndComposition(maskTempCtx, maskLayer.info);
      addShadow(maskTempCtx, maskLayer.info);
      addFilters(maskTempCtx, maskLayer.info);

      if (maskLayer.info.erasingMask) {
        maskTempCtx.globalCompositeOperation = 'destination-out';
      } else {
        maskTempCtx.globalCompositeOperation = 'source-atop';
      }

      maskTempCtx.drawImage(maskLayer.previewTempCanvas || maskLayer.canvas, 0, 0);

      maskTempCtx.restore();
    })

    mainLayer.maskTempCanvas = maskTempCanvas;
    mainLayer.maskTempCtx = maskTempCtx;

  }



  const drawOnLayer = async ({
    layerCtx, 
    layerData, 
    strokes, 
    difficulty, 
    bannedUsers,
    preview,
    clear,
  }) => {

    if (!strokes[0]) { return; }
    
    const layerId = layerData.id;
    const renderStartTime = Date.now();
    let layerLastRenderedStroke;
    let lastHintTime = 0;

    convy.soft.canvas.width = layerCtx.canvas.width;
    convy.soft.canvas.height = layerCtx.canvas.height;
  
    let i = 0;

    if (clear) {
      strokes = [];
    } else if (game?.mode === 'line') { 
      strokes = strokes
      .filter(stroke => (!stroke.cancelled && !stroke.hidden))
      .slice(-1);
    }

    for (const stroke of strokes) {

      stroke.rendered = true; 

      if (bannedUsers.includes(stroke.userId)) { return; }

      if (stroke.cancelled || stroke.hidden) continue;

      debug.metrics.redraw.strokes++;
      history.lastStrokeRenderTime = Date.now();
      history.lastRenderedStroke = stroke;
      layerLastRenderedStroke = stroke;
  
      const layerCache = history.layerImageCache[layerId];
  
      if (layerCache && layerCache.get(stroke.time)) {
        // Если для штриха уже есть кеш – используем offscreen-канвас
        const strokeCache = layerCache.get(stroke.time);
        layerCtx.drawImage(strokeCache.canvas, 0, 0);
        debug.metrics.redraw.strokes--;
      } else if (stroke.type === 'background') {
        drawBackground({ context: layerCtx, color: stroke.color });
      } else if (stroke.type === 'fill') {
        makeFill({ context: layerCtx, stroke });
      } else if (stroke.type === 'stroke') {
        await drawStroke(stroke, layerCtx);
      }
  
      i++;
      const currentTime = Date.now();
      if (currentTime - renderStartTime > 500 && currentTime - lastHintTime > 1000) {
        lastHintTime = currentTime;
        showTemporaryHint(
          `⏳ ${layerData.name}: ${i} / ${strokes.length}`,
          { force: true, duration: 800 }
        );
        forceRender();
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  
    // Определяем порог кеширования: если включён режим moreCache – 2, иначе 4
    const cacheThreshold = info.user.moreCache ? 20 : 40;
 
    if (
      !preview 
      && layerLastRenderedStroke
      && difficulty >= cacheThreshold
      ) {
      const lastStroke = layerLastRenderedStroke;
      setTimeout(() => {
        window.croco?.log(`::-- ${lastStroke.brush}: ${difficulty}/${cacheThreshold} =${lastStroke.time}`);

        // strokes.forEach(stroke => { stroke.rendered = true; });
        saveCache({
          layerCtx, 
          lastStroke, 
          layerId, 
        });
      }, 0);

    }
  
    const layer = ref.convy.methods.getLayer(layerId);
    layer.ready = Date.now();

  };
  

  
  function transformation (mainCtx, layerInfo) {

    addOpacityAndComposition(mainCtx, layerInfo);
    addShadow(mainCtx, layerInfo);
    addFilters(mainCtx, layerInfo);
    translatePosition (mainCtx, layerInfo);

  }

  function translatePosition (mainCtx, layerInfo) {
      // Вычисляем центр основного канваса
      const centerX = mainCtx.canvas.width / 2;
      const centerY = mainCtx.canvas.height / 2;
      mainCtx.translate(centerX, centerY);
      mainCtx.translate(layerInfo.offset?.x || 0, layerInfo.offset?.y || 0);
      if (layerInfo.rotation) {
        mainCtx.rotate((layerInfo.rotation * Math.PI) / 180);
      }
      if (layerInfo.scale && layerInfo.scale !== 1) {
        mainCtx.scale(layerInfo.scale, layerInfo.scale);
      }
  }

  function addShadow (mainCtx, layerInfo) {

    // Если заданы параметры тени, применяем их:
    if (layerInfo.shadowOn) {
      mainCtx.shadowColor = layerInfo.shadowColor || '#000000FF';
      mainCtx.shadowBlur = layerInfo.shadowBlur || 0;
      mainCtx.shadowOffsetX = layerInfo.shadowOffsetX || 0;
      mainCtx.shadowOffsetY = layerInfo.shadowOffsetY || 0;
    }
    
  }

  function addOpacityAndComposition (mainCtx, layerInfo) {

    // Устанавливаем прозрачность и режим композитинга
    mainCtx.globalAlpha = layerInfo.opacity !== undefined ? layerInfo.opacity : 1;
    mainCtx.globalCompositeOperation = layerInfo.composition || 'source-over';
    
  }

  function addFilters (mainCtx, layerInfo) {

    if (layerInfo.filterOn) {
      let filterParts = [];
      
      // brightness: по умолчанию 1
      if (typeof layerInfo.filterBrightness !== 'undefined' && layerInfo.filterBrightness !== 1) {
        filterParts.push(`brightness(${layerInfo.filterBrightness})`);
      }
      
      // contrast: по умолчанию 1
      if (typeof layerInfo.filterContrast !== 'undefined' && layerInfo.filterContrast !== 1) {
        filterParts.push(`contrast(${layerInfo.filterContrast})`);
      }
      
      // blur: по умолчанию 0
      if (typeof layerInfo.filterBlur !== 'undefined' && layerInfo.filterBlur !== 0) {
        filterParts.push(`blur(${layerInfo.filterBlur}px)`);
      }
      
      // grayscale: по умолчанию 0%
      if (typeof layerInfo.filterGrayscale !== 'undefined' && layerInfo.filterGrayscale !== 0) {
        filterParts.push(`grayscale(${layerInfo.filterGrayscale}%)`);
      }
      
      // hue-rotate: по умолчанию 0deg
      if (typeof layerInfo.filterHueRotate !== 'undefined' && layerInfo.filterHueRotate !== 0) {
        filterParts.push(`hue-rotate(${layerInfo.filterHueRotate}deg)`);
      }
      
      // invert: по умолчанию 0%
      if (typeof layerInfo.filterInvert !== 'undefined' && layerInfo.filterInvert !== 0) {
        filterParts.push(`invert(${layerInfo.filterInvert}%)`);
      }
      
      // opacity: по умолчанию 1
      if (typeof layerInfo.filterOpacity !== 'undefined' && layerInfo.filterOpacity !== 1) {
        filterParts.push(`opacity(${layerInfo.filterOpacity})`);
      }
      
      // saturate: по умолчанию 1
      if (typeof layerInfo.filterSaturate !== 'undefined' && layerInfo.filterSaturate !== 1) {
        filterParts.push(`saturate(${layerInfo.filterSaturate})`);
      }
      
      // sepia: по умолчанию 0%
      if (typeof layerInfo.filterSepia !== 'undefined' && layerInfo.filterSepia !== 0) {
        filterParts.push(`sepia(${layerInfo.filterSepia}%)`);
      }
      
      if (filterParts.length) {
        mainCtx.filter = filterParts.join(' ');
      }
    
    }
  }


  const renderForServer = async (pref = {})=>{

    try {

      window.croco?.log('📨💻render for server start');
      // await redraw()
      const lastStroke = history.lastRenderedStroke;
      const imagePng = convy.main.ref.toDataURL('image/png');
      window.croco?.log('✔️💻render for server dataUrl');

      return {
        lastStroke,
        imagePng,
      }
      
    } catch (e) {
      return { error: {
        message: e.message,
        stack: e.stack,
      },}
    }


  }

  const updatePixelGrid = () => {

    const { activeTool, brushType, eraserType } = useStore.getState();
    const { showPixelGrid, pixelSize } = brush.methods.brushSetsFun('pixels');
    const { showCommonGrid, gridSize } = brush.methods.brushSetsFun('common');
    const gridCtx = convy.grid.ctx;

    if (!gridCtx) return;

    const needToShowPixelGrid = (brushType === 'pixels') ||
    (activeTool === 'eraser' && eraserType === 'pixels');

    const currentGridSize = needToShowPixelGrid && showPixelGrid ? pixelSize : gridSize;

    const needToShowGrid = showCommonGrid || (showPixelGrid && needToShowPixelGrid)

    if (convy.grid.isDrawn) {
      gridCtx.clearRect(0, 0, gridCtx.canvas.width, gridCtx.canvas.height);
      convy.grid.isDrawn = false;
    }

    if (needToShowGrid) {
      const activeLayer = convy.methods.getActiveLayer();
      drawPixelGridOnContext(gridCtx, activeLayer, currentGridSize);
      convy.grid.isDrawn = true;
    }

  };


  const drawPixelGridOnContext = (ctx, activeLayer, currentGridSize) => {
    const layerInfo = activeLayer.info;

    if (!currentGridSize || currentGridSize <= 0) return;
    
    ctx.save();
    
    transformation(ctx, layerInfo);

    const canvasWidth = activeLayer.canvas.width;
    const canvasHeight = activeLayer.canvas.height;
    const scale = layerInfo.scale || 1;

    // Сдвигаем начало координат сетки, чтобы оно совпало с левым верхним углом слоя
    ctx.translate(-canvasWidth / 2, -canvasHeight / 2);


    const backgroundColor = info.game?.settings?.backgroundColor || '#FFFFFFFF';
    const backgroundColorLuminance = chroma(backgroundColor).luminance();

    ctx.strokeStyle = backgroundColorLuminance > 0.3 ? 'rgba(0, 0, 0, 0.25)' : 'rgba(200, 200, 200, 0.75)';
    const baseLineWidth = backgroundColorLuminance > 0.5 ? 0.5 / scale : 0.5 / scale;
    ctx.setLineDash([]);

    const drawLines = (predicate, lineWidth) => {
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      // Вертикальные линии
      for (let i = 0; ; i++) {
        const x = i * currentGridSize;
        if (x > canvasWidth) break;
        if (predicate(i)) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvasHeight);
        }
      }
      // Горизонтальные линии
      for (let i = 0; ; i++) {
        const y = i * currentGridSize;
        if (y > canvasHeight) break;
        if (predicate(i)) {
          ctx.moveTo(0, y);
          ctx.lineTo(canvasWidth, y);
        }
      }
      ctx.stroke();
    };

    const decimalGrid = true;

    if (decimalGrid) {
      // Сначала тонкие линии (не кратные 10)
      drawLines(i => i % 10 !== 0, baseLineWidth);
      // Затем толстые (кратные 10, включая нулевую)
      drawLines(i => i % 10 === 0, baseLineWidth * 2);
    } else {
      // Все линии одной толщины
      drawLines(() => true, baseLineWidth);
    }
    
    ctx.restore();
}


  return {
    drawStroke,
    redraw,
    renderForServer,
    updatePixelGrid,
  };
};
